'use client';

import type { MouseEvent } from 'react';
import { motion } from 'framer-motion';
import CmsImage from '@/components/ui/CmsImage';
import { scrollToHashTarget } from '@/components/util/GsapSmoothScroller';
import { localizeUrl, stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiMediaUrl } from '@/lib/strapi-media-url';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import type { CommunityNavCardItem } from '@/types/community';
import { Reveal } from '../lib/community-reveal';

interface CommunityNavCardsProps {
  locale: string;
  zone: {
    heading: string | null;
    navCardItem: CommunityNavCardItem[];
  };
}

/** Resolve CMS buttonUrl into a hash for in-page scroll, or a localized page URL. */
function resolveNavHref(buttonUrl: string | null | undefined, locale: string): string | null {
  const raw = buttonUrl?.trim();
  if (!raw || raw === '#!') return null;
  if (raw.startsWith('#')) return raw;
  if (/^(https?:|mailto:|tel:)/i.test(raw)) return raw;
  // Bare section id from CMS (e.g. "education") → in-page hash matching zigzag sectionId
  if (!raw.includes('/')) return `#${raw.replace(/^#/, '')}`;
  return localizeUrl(raw, locale);
}

function getInPageHash(href: string): string | null {
  const hashIndex = href.indexOf('#');
  if (hashIndex < 0) return null;
  const hash = href.slice(hashIndex);
  return hash.length > 1 ? hash : null;
}

function slugifyFallbackHash(card: CommunityNavCardItem): string {
  return (card.pillText || card.title || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(?:^-+)|(?:-+$)/g, '');
}

function PillarNavCard({
  card,
  index,
  locale,
}: {
  card: CommunityNavCardItem;
  index: number;
  locale: string;
}) {
  const imageUrl = getStrapiMediaUrl(card.image, 'medium');
  const button = Array.isArray(card.button) ? card.button[0] : undefined;
  const buttonLabel = button?.buttonText?.trim() || button?.ariaLabel?.trim() || 'Explore';
  const fallbackHash = slugifyFallbackHash(card);
  const href =
    resolveNavHref(button?.buttonUrl, locale) ?? (fallbackHash ? `#${fallbackHash}` : null);
  // Always show Explore when we have a scroll/nav target (do not require buttonType).
  const showLink = Boolean(href);
  const isExternal = Boolean(href && /^(https?:|mailto:|tel:)/i.test(href));
  const inPageHash = href ? getInPageHash(href) : null;
  const linkProps = getCmsButtonLinkProps(button, isExternal ? '_blank' : undefined);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!inPageHash || linkProps.target === '_blank') return;
    event.preventDefault();
    scrollToHashTarget(inPageHash, true, true);
  };

  return (
    <motion.article
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-stone-100 bg-white text-left shadow-sm transition-shadow hover:shadow-lg"
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
    >
      <figure className="relative m-0 h-48 w-full shrink-0 overflow-hidden">
        <CmsImage
          src={imageUrl}
          alt={card.image?.alternativeText || card.title || ''}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-105"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
          fallbackSrc={MEDIA_PLACEHOLDER}
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"
          aria-hidden
        />
        {card.pillText ? (
          <span className="absolute left-3 top-3 rounded-full bg-black/40 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white backdrop-blur-sm">
            {card.pillText}
          </span>
        ) : null}
        {card.title ? <figcaption className="sr-only">{card.title}</figcaption> : null}
      </figure>

      <div className="flex flex-1 flex-col p-5">
        {card.title ? (
          <h3 className="text-sm font-bold leading-tight text-[#1a1a1a] sm:text-base">
            {card.title}
          </h3>
        ) : null}
        {card.description ? (
          <p className="mt-2 flex-1 text-xs leading-5 text-stone-500 sm:text-sm sm:leading-6">
            {stripHtml(card.description)}
          </p>
        ) : null}
        {showLink && href ? (
          <a
            href={href}
            onClick={handleClick}
            {...linkProps}
            aria-label={button?.ariaLabel?.trim() || `Explore ${card.title ?? ''}`}
            className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#ed1c22] transition-all duration-200 hover:gap-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ed1c22]"
          >
            <span>{buttonLabel}</span>
            <svg
              viewBox="0 0 16 16"
              fill="none"
              className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        ) : null}
      </div>
    </motion.article>
  );
}

export default function CommunityNavCards({ locale, zone }: CommunityNavCardsProps) {
  const cards = zone.navCardItem ?? [];
  if (cards.length === 0) return null;

  return (
    <section
      className="bg-white px-4 sm:px-6 md:px-16 lg:px-24"
      aria-labelledby="four-pillars-heading"
    >
      <div className="container mx-auto max-sm:px-4">
        {zone.heading ? (
          <div className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <Reveal direction="up" delay={80}>
              <h2
                id="four-pillars-heading"
                className="text-[1.75rem] font-bold leading-[1.12] tracking-tight text-[#1a1a1a] sm:text-4xl"
              >
                {zone.heading}
              </h2>
            </Reveal>
          </div>
        ) : null}

        <nav aria-label="Community programme pillars">
          <ul className="grid list-none grid-cols-1 gap-5 p-0 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((card, index) => (
              <li key={card.id} className="h-full">
                <PillarNavCard card={card} index={index} locale={locale} />
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
}
